import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect, useMemo } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { z } from "zod";

import { useConfiguration } from "~/contexts/configuration";
import { useGiftCardMedia } from "~/contexts/giftcard-media";
import { useCartProduct } from "~/hooks/use-cart";
import { useFormTranslations } from "~/hooks/use-form-translations";
import { algoliaAddToBag } from "~/services/algolia-utils";
import Logger from "~/services/logger/logger";
import { MarketingCloudService } from "~/services/marketing-cloud.service";
import CrossCountry from "~/types/cross-country";
import intlUtils from "~/utils/intl-utils";
import productUtils from "~/utils/product-utils";

import Button from "../common/button";
import Checkbox from "../form/checkbox";
import InputField from "../form/input-field";
import SelectGeneric, { SelectOption } from "../form/select-generic";
import styles from "./product-gift-addtobag.module.scss";

const GiftCardPhysical = z.object({
  isVirtual: z.literal(false),
  isGift: z.literal(false),
  amount: z.number(),
  customAmount: z
    .number({ invalid_type_error: "error.amountRequired" })
    .min(10, "error.valueTooShort")
    .max(999, "error.valueTooLong")
    .optional(),
});

const GiftCardPhysicalisGift = z.object({
  isVirtual: z.literal(false),
  isGift: z.literal(true),
  amount: z.number(),
  customAmount: z
    .number({ invalid_type_error: "error.amountRequired" })
    .min(10, "error.valueTooShort")
    .max(999, "error.valueTooLong")
    .optional(),
  senderName: z.string(),
  recipientName: z.string(),
});

const virtualGiftcardSchema = z.object({
  isVirtual: z.literal(true),
  isGift: z.literal(false),
  amount: z.number(),
  customAmount: z
    .number({ invalid_type_error: "error.amountRequired" })
    .min(10, "error.valueTooShort")
    .max(999, "error.valueTooLong")
    .optional(),
  recipient: z.string().email("error.invalidEmail"),
  imageUrl: z.string(),
  cardName: z.string(),
});

const virtualGiftGiftcardSchema = virtualGiftcardSchema.extend({
  isGift: z.literal(true),
  senderName: z.string(),
  recipientName: z.string(),
  message: z.string(),
});

const addSuperRefiner = function <Augmentation extends z.ZodType<{ recipient: string }>>(
  baseSchema: Augmentation
): z.ZodEffects<Augmentation> {
  return baseSchema.superRefine(async (data, ctx) => {
    try {
      baseSchema.parse(data);
      return await MarketingCloudService.validateEmail(data.recipient)
        .then(() => !0)
        .catch(() =>
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "error.marketing_cloud_invalid_email",
            path: ["recipient"],
          })
        );
    } catch {
      Logger.instance.debug("Form already invalid, skipping refinement");
    }
  });
};

const schema = z.union([
  GiftCardPhysical,
  GiftCardPhysicalisGift,
  addSuperRefiner(virtualGiftcardSchema),
  addSuperRefiner(virtualGiftGiftcardSchema),
]);

export type GiftCardVirtualType = z.infer<typeof virtualGiftcardSchema>;
export type GiftCard = z.infer<typeof schema>;

type Props = {
  root: CrossCountry.DetailedProduct;
};

export default function ProductGiftAddtobag(props: Props) {
  const t = useTranslations();
  const { algoliaIndexId, configuration } = useConfiguration();
  const priceFormatter = intlUtils.usePriceFormatter();
  const { getFieldError } = useFormTranslations();

  const { addGiftCard } = useCartProduct(props.root.product_id);

  const { selectedIndex } = useGiftCardMedia();

  const selectedVariant = useMemo(() => {
    return props.root.giftCardConfig?.fields.virtualCards[selectedIndex];
  }, [props.root.giftCardConfig?.fields.virtualCards, selectedIndex]);

  const isVirtual = productUtils.isVirtualProduct(props.root);

  const {
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    clearErrors,
    setValue,
    register,
    watch,
    resetField,
  } = useForm<GiftCard>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
  });

  const isGift = watch("isGift");
  const watchAmount = watch("amount");

  useEffect(() => {
    if (watchAmount !== 0) {
      resetField("customAmount");
    }
  }, [resetField, watchAmount]);

  useEffect(() => {
    setValue("isVirtual", isVirtual);
    if (isVirtual) {
      setValue("imageUrl", `https:${selectedVariant?.fields.cardImage.fields?.file.url}`);
      setValue("cardName", selectedVariant?.fields.name || "");
    }
  }, [isVirtual, selectedVariant, setValue]);

  async function addToBag(fields: GiftCard) {
    addGiftCard.mutate(fields);
    algoliaAddToBag(
      algoliaIndexId,
      props.root.product_id,
      { price: fields.customAmount || fields.amount, currency: configuration.CurrencyID },
      1
    );
    // TODO need to understand if we want to reset the fields
    // reset({ recipient: "", isVirtual: isVirtual });
  }

  const availableAmounts = useMemo(() => {
    let amounts: SelectOption[] = [];
    if (props.root.giftCardConfig) {
      amounts =
        props.root.giftCardConfig?.fields.availableAmounts?.map((amount) => ({
          value: Number(amount),
          label: priceFormatter.currency(Number(amount), { currency: props.root.currency_id }),
        })) ?? [];
    }
    amounts.push({ label: t("generic.giftcard.otherimprt"), value: 0 });
    return amounts;
  }, [priceFormatter, props.root.currency_id, props.root.giftCardConfig, t]);

  return (
    <form className={styles.giftAction} onSubmit={handleSubmit(addToBag)}>
      <SelectGeneric
        {...register("amount", { valueAsNumber: true })}
        label={t("generic.giftcard.chooseamount")}
        options={availableAmounts}
        isOptional
      />
      {watchAmount === 0 && (
        <InputField
          {...register("customAmount", { valueAsNumber: true })}
          label={t("generic.giftcard.customvalue")}
          type="number"
          error={getFieldError(errors.customAmount?.message, {
            fieldName: t("generic.giftcard.customvalue"),
            maxLength: 999,
            minLength: 10,
          })}
          onClearErrors={() => clearErrors("customAmount")}
        />
      )}
      {isVirtual && (
        <InputField
          {...register("recipient")}
          label={t("generic.giftcard.recipient")}
          type="email"
          error={
            (errors as FieldErrors<GiftCardVirtualType>).recipient
              ? t((errors as FieldErrors<GiftCardVirtualType>).recipient?.message)
              : undefined
          }
          onClearErrors={() => clearErrors("recipient")}
        />
      )}
      <Checkbox {...register("isGift")} label={t("generic.giftcard.isgift")} type="checkbox" />

      {isGift && (
        <>
          <InputField {...register("senderName")} label={t("generic.giftcard.sendername")} type="text" isOptional />
          <InputField
            {...register("recipientName")}
            label={t("generic.giftcard.recipientname")}
            type="text"
            isOptional
          />
          {isVirtual && (
            <InputField {...register("message")} label={t("generic.giftcard.yourmessage")} type="text" isOptional />
          )}
        </>
      )}

      <Button type="submit" variant="primary" loading={isSubmitting} disabled={isSubmitting || !isValid}>
        {t("generic.add_to_bag")}
      </Button>
    </form>
  );
}
